<dialog #dialog aria-labelledby="player-profile-title" (cancel)="$event.preventDefault(); dismissible() && close()" (click)="backdrop($event)"
  class="bg-white text-slate-900 dark:bg-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl">
  <div class="p-5">
    @if (loading() || error()) {
      <div class="flex justify-end">
        <button type="button" aria-label="Cerrar ficha del jugador" autofocus class="p-2 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800" (click)="close()">✕</button>
      </div>
    }
    @if (loading()) {
      <h2 id="player-profile-title" class="font-bold">{{ playerName() }}</h2>
      <p role="status" class="py-10 text-center text-sm text-slate-500">Cargando ficha…</p>
    } @else if (error()) {
      <h2 id="player-profile-title" class="font-bold">{{ playerName() }}</h2>
      <p role="alert" class="py-6 text-center text-sm text-rose-500">{{ error() }}</p>
      <button type="button" class="px-3 py-2 rounded-xl bg-indigo-600 text-white cursor-pointer" (click)="retry()">Reintentar</button>
    } @else if (profile(); as player) {
      <div class="flex items-start justify-between gap-4 pb-4 border-b border-slate-100 dark:border-slate-800">
        <div class="flex items-center gap-3.5 min-w-0">
          <div class="w-12 h-12 rounded-2xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center p-1.5 shrink-0 shadow-xs">
            @if (clubShield(player); as shield) {
              <img [src]="shield" [alt]="player.realClub?.name || 'Club'" (error)="onCrestError($event)" class="w-full h-full object-contain" referrerpolicy="no-referrer" />
            } @else {
              <span class="text-xs font-bold text-slate-500">{{ player.realClub?.shortName || 'CLB' }}</span>
            }
          </div>
          <div class="min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <h2 id="player-profile-title" class="text-lg font-bold text-slate-900 dark:text-white leading-tight break-words">{{ player.displayName }}</h2>
              <span class="px-2 py-0.5 rounded text-[10px] font-bold border" [ngClass]="positionBadgeClass(player.position)">{{ position(player.position) }}</span>
              @for (secondary of player.secondaryPositions; track secondary) {
                <span class="px-2 py-0.5 rounded text-[10px] font-bold border" [ngClass]="positionBadgeClass(secondary)">{{ position(secondary) }}</span>
              }
            </div>
            <div class="flex items-center gap-2 mt-1 text-xs text-slate-500 dark:text-slate-400 flex-wrap">
              <span class="font-semibold text-slate-700 dark:text-slate-300">{{ player.realClub?.name || 'Sin club disponible' }}</span>
              <span>•</span><span>{{ player.age === null ? 'Edad no disponible' : player.age + ' años' }}</span>
            </div>
          </div>
        </div>
        <button type="button" aria-label="Cerrar ficha del jugador" class="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 shrink-0 cursor-pointer" (click)="close()">✕</button>
      </div>

      @if (player.unavailabilities; as unavailabilities) {
        @if (unavailabilities.length) {
          <section class="mt-5 space-y-2" aria-label="Estado de disponibilidad">
            <h3 class="text-xs font-bold uppercase text-rose-600 dark:text-rose-400">Estado de disponibilidad: No disponible</h3>
            @for (unavailability of unavailabilities; track $index) {
              <div class="p-3 rounded-xl border border-rose-200 dark:border-rose-900/60 bg-rose-50 dark:bg-rose-950/30 text-rose-800 dark:text-rose-300 text-xs">
                <div class="font-bold">{{ getUnavailabilityTypeLabel(unavailability.type) }}: {{ getUnavailabilityReasonLabel(unavailability.reason) }}</div>
                @if (unavailability.startDate) { <div class="mt-1">Inicio: {{ unavailability.startDate }}</div> }
                @if (unavailability.expectedEndDate) { <div class="mt-1">Baja prevista hasta: {{ unavailability.expectedEndDate }}</div> }
              </div>
            }
          </section>
        } @else {
          <div class="mt-5 p-2.5 rounded-xl border border-emerald-200 dark:border-emerald-900/60 bg-emerald-50/60 dark:bg-emerald-950/30 text-xs text-emerald-800 dark:text-emerald-300 flex items-center gap-2">
            <span aria-hidden="true">✓</span><span class="font-semibold">Disponible para competir</span>
          </div>
        }
      } @else {
        <p class="mt-5 text-xs text-slate-500">Estado de disponibilidad no disponible.</p>
      }
      <h3 class="mt-5 text-xs font-bold uppercase tracking-wide text-slate-500">Métricas de rendimiento y mercado</h3>
      <div class="grid grid-cols-3 gap-2 mt-3 p-3 bg-slate-50 dark:bg-slate-950/70 rounded-xl border border-slate-100 dark:border-slate-800/80 text-xs">
        <div><div class="text-[10px] text-slate-500">Valor Mercado</div><div class="font-bold font-mono mt-0.5">{{ marketMillions(player.marketValue) }}</div></div>
        <div><div class="text-[10px] text-slate-500">Media</div><div class="font-bold font-mono mt-0.5">{{ number(player.averagePoints) }}</div></div>
        <div title="Media de las últimas tres jornadas cerradas con puntuación disponible"><div class="text-[10px] text-slate-500">Últ. 3</div><div class="font-bold text-emerald-600 dark:text-emerald-400 font-mono mt-0.5">{{ number(player.last3AveragePoints ?? null) }}</div></div>
      </div>
      @if (chart().bars.length) {
        <section class="mt-6">
          <h3 class="text-xs font-bold text-slate-500 uppercase mb-3">Evolución</h3>
          <div class="overflow-x-auto rounded-xl border border-slate-100 dark:border-slate-800 p-2">
            <svg viewBox="0 0 700 270" class="w-full h-auto" role="img" aria-label="Puntos y valor de mercado por jornada">
              <line x1="20" x2="680" [attr.y1]="chart().zero" [attr.y2]="chart().zero" stroke="#e2e8f0" stroke-dasharray="4" />
              @for (bar of chart().bars; track bar.annualMatchdayNumber) {
                @if (bar.points !== null) {
                  <rect [attr.x]="bar.x - 5" [attr.y]="bar.y" width="10" [attr.height]="bar.height || 2" rx="2" class="fill-indigo-500" />
                }
              }
              @for (path of chart().paths; track $index) {
                <path [attr.d]="path" fill="none" stroke="#10b981" stroke-width="2.5" />
              }
              @for (bar of chart().bars; track bar.annualMatchdayNumber) {
                @if (bar.valueY !== null) {
                  <circle [attr.cx]="bar.x" [attr.cy]="bar.valueY" r="3" fill="#10b981" />
                }
                @if (chart().bars.length <= 16 || $index % 3 === 0 || $last) {
                  <text [attr.x]="bar.x" y="247" text-anchor="middle" font-size="12">J{{ bar.annualMatchdayNumber }}</text>
                }
              }
            </svg>
            <p class="text-[10px] text-slate-500"><span class="text-indigo-500">■ Puntos</span> · <span class="text-emerald-600">● Valor de mercado</span></p>
          </div>
          <table class="w-full mt-4 text-xs">
            <thead>
              <tr class="text-slate-400 border-b border-slate-100 dark:border-slate-800">
                <th class="py-2 text-left">Jornada</th>
                <th class="py-2 text-right">Puntos</th>
                <th class="py-2 text-right">Valor</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
              @for (day of chart().bars; track day.annualMatchdayNumber) {
                <tr>
                  <td class="py-2 font-mono">J{{ day.annualMatchdayNumber }}</td>
                  <td class="py-2 text-right font-bold">{{ number(day.points) }}</td>
                  <td class="py-2 text-right text-emerald-600">{{ money(day.marketValue) }}</td>
                </tr>
              }
            </tbody>
          </table>
        </section>
      }
    }
    @if (ownedPlayer(); as op) {
      @if (!op.isAmateur && isListingFormOpen()) {
        <div class="mt-4 p-3 rounded-xl border border-emerald-200 dark:border-emerald-800/60 bg-emerald-50/50 dark:bg-emerald-950/20 space-y-2">
          <div class="flex items-center justify-between">
            <span class="text-xs font-bold text-slate-800 dark:text-slate-200">Poner a la venta</span>
            <span class="text-[11px] text-slate-500 dark:text-slate-400">Precio en millones (€)</span>
          </div>
          <div class="flex items-center gap-2">
            <div class="relative flex-1">
              <input
                type="number"
                step="0.1"
                min="0.1"
                [value]="askingPriceInput()"
                (input)="onAskingPriceChange($event)"
                [disabled]="isPending()"
                class="w-full px-3 py-1.5 pr-10 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-sm font-mono font-bold text-emerald-600 dark:text-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                placeholder="5.0"
              />
              <span class="absolute right-3 top-1.5 text-xs font-mono font-semibold text-slate-400 pointer-events-none">M€</span>
            </div>
            <button
              type="button"
              (click)="isListingFormOpen.set(false)"
              [disabled]="isPending()"
              class="px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="button"
              (click)="confirmListing()"
              [disabled]="!game.canAction('sellPlayer') || (isPending() || !askingPriceInput() || askingPriceInput()! <= 0)"
              class="px-3.5 py-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg cursor-pointer shadow-xs disabled:opacity-50"
            >
              {{ isPending() ? 'Publicando…' : 'Publicar en Mercado' }}
            </button>
          </div>
        </div>
      }
    }
    <div class="mt-4 pt-3 flex items-center justify-between gap-3 border-t border-slate-100 dark:border-slate-800">
      <div>
        @if (ownedPlayer(); as op) {
          @if (!op.isAmateur && !isListingFormOpen()) {
            @if (!op.listedForTransfer) {
              <button
                type="button"
                [disabled]="isPending()"
                (click)="openListingForm(op)"
                class="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl cursor-pointer shadow-xs transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                <span>Poner transferible</span>
              </button>
            } @else {
              <button
                type="button"
                [disabled]="isPending()"
                (click)="withdrawFromSale(op)"
                class="px-3.5 py-2 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-900 text-xs font-bold rounded-xl cursor-pointer transition-colors disabled:opacity-50"
              >
                {{ isPending() ? 'Retirando…' : 'Retirar de venta (' + (op.askingPrice ?? '-') + ' M€)' }}
              </button>
            }
          }
        }
      </div>
      <button type="button" (click)="close()" class="px-4 py-2 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs font-bold rounded-xl cursor-pointer">Cerrar</button>
    </div>
  </div>
</dialog>
